import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Download, FileText, Package, Users, HelpCircle, ArrowLeft, Sparkles, Heart, MessageCircle } from "lucide-react";

// Keywords map so each toolkit pulls the most relevant community wins
const TOOLKIT_KEYWORDS = {
  "p-d-1":  ["gap", "empty", "orphan", "pricing", "pricelabs", "weekend"],
  "p-d-2":  ["message", "guest comm", "template", "inbox", "review ask", "communication"],
  "p-d-3":  ["clean", "sop", "turnover", "damage", "aircover"],
  "p-d-4":  ["owner", "pitch", "outreach", "dm", "contract", "sign"],
  "p-d-5":  ["listing", "title", "photo", "ctr", "description", "clicks"],
  "p-d-6":  ["launch", "first listing", "90 day", "blueprint", "startup", "beginner"],
  "p-d-7":  ["pricing", "adr", "pricelabs", "wheelhouse", "revenue"],
  "p-d-8":  ["scale", "va", "team", "unit", "portfolio", "hiring"],
  "p-d-9":  ["owner", "first", "sign", "contract", "pipeline", "outreach"],
  "p-d-10": ["zapier", "automate", "hostaway", "automation", "workflow"],
  "p-d-11": ["business", "scale", "portfolio", "brand", "exit"],
  "p-d-12": ["planner", "goal", "weekly", "monthly", "accountability", "mentorship"],
  "p-d-13": ["everything", "business", "bundle", "full", "scale"],
};

function matchesKeywords(content, keywords) {
  const t = (content || "").toLowerCase();
  return keywords.some((kw) => t.includes(kw));
}

export default function ToolkitDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [owned, setOwned] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [winsPosts, setWinsPosts] = useState([]);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => toast.error("Toolkit not found."))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    api.get("/community/posts?category=wins")
      .then((r) => setWinsPosts(r.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) { setOwned(false); return; }
    api.get("/me/toolbox")
      .then((r) => setOwned((r.data?.items || []).some((p) => p.id === id)))
      .catch(() => {});
  }, [user, id]);

  const matchedQuotes = useMemo(() => {
    const keywords = TOOLKIT_KEYWORDS[id] || [];
    const matched = winsPosts.filter((p) => matchesKeywords(p.content, keywords));
    // sort by likes desc; fall back to top-3 wins overall if no keyword matches
    const pool = matched.length >= 2
      ? matched
      : [...winsPosts].sort((a, b) => (b.likes || 0) - (a.likes || 0));
    return pool.sort((a, b) => (b.likes || 0) - (a.likes || 0)).slice(0, 3);
  }, [winsPosts, id]);

  const buy = async () => {
    if (!user) return nav("/login");
    setBusy(true);
    try {
      const { data } = await api.post("/stripe/create-checkout-session", {
        kind: "product",
        item_id: product.id,
        success_url: `${window.location.origin}/toolbox?purchased=${product.id}`,
        cancel_url: `${window.location.origin}/toolkits/${product.id}`,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed.");
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white min-h-screen">
        <Navbar />
        <div className="py-24 text-center text-neutral-500">Loading toolkit…</div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="bg-white min-h-screen">
        <Navbar />
        <section className="py-24 max-w-2xl mx-auto px-6 text-center">
          <h1 className="font-display text-4xl mb-4">Toolkit not found.</h1>
          <Link to="/courses">
            <Button className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white">Back to catalog</Button>
          </Link>
        </section>
        <Footer />
      </div>
    );
  }

  const previewPages = product.preview_pages || [];
  const whatsInside = product.whats_inside || [];
  const whoFor = product.who_for || [];
  const faq = product.faq || [];

  return (
    <div className="bg-white min-h-screen">
      <Navbar />

      <section className="py-16 max-w-6xl mx-auto px-6 lg:px-10">
        <Link
          to="/courses"
          data-testid="toolkit-back-link"
          className="inline-flex items-center text-sm text-neutral-500 hover:text-brand-pink mb-10"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" /> All toolkits
        </Link>

        {/* HERO */}
        <div className="grid lg:grid-cols-[1fr_380px] gap-14 items-start">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-brand-pink mb-4">
              <Package className="w-3 h-3" /> {product.category || "Premium Toolkit"}
            </div>
            <h1 className="font-display text-5xl md:text-6xl leading-[1.05] mb-6">{product.name}</h1>
            <p className="text-lg text-neutral-600 leading-relaxed max-w-xl">
              {product.long_description || product.description}
            </p>

            {/* stats row */}
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-xl">
              <StatBox label="Pages" value={product.pages_count || "—"} />
              <StatBox label="Lifetime access" value="Yes" />
              <StatBox label="Updates" value="Free" />
            </div>
          </div>

          {/* BUY CARD */}
          <aside
            data-testid="toolkit-buy-card"
            className="rounded-3xl bg-brand-alabaster border border-neutral-100 p-7 lg:sticky lg:top-28 shadow-sm"
          >
            <div className="text-xs uppercase tracking-widest text-brand-pink mb-3">One-time purchase</div>
            <div className="flex items-baseline gap-3 mb-6">
              <span className="font-display text-5xl">${product.price}</span>
              <span className="text-neutral-400 text-sm">lifetime</span>
            </div>

            {owned ? (
              <Link to="/toolbox">
                <Button
                  data-testid="toolkit-owned-cta"
                  className="w-full rounded-full h-12 bg-black hover:bg-black/85 text-white"
                >
                  <Check className="w-4 h-4 mr-2" /> You own this — open Toolbox
                </Button>
              </Link>
            ) : (
              <Button
                onClick={buy}
                disabled={busy}
                data-testid="toolkit-buy-cta"
                className="w-full rounded-full h-12 bg-brand-pink hover:bg-brand-pink/90 text-white btn-pink-glow"
              >
                <Download className="w-4 h-4 mr-2" /> {busy ? "Opening checkout…" : "Get the toolkit"}
              </Button>
            )}

            <ul className="mt-6 space-y-2 text-sm text-neutral-600">
              <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-brand-pink mt-1 flex-shrink-0" /> Lifetime access, re-downloadable</li>
              <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-brand-pink mt-1 flex-shrink-0" /> Lives in your Toolbox after purchase</li>
              <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-brand-pink mt-1 flex-shrink-0" /> Free updates as Kristy improves it</li>
            </ul>
          </aside>
        </div>

        {/* WHO IT'S FOR */}
        {whoFor.length > 0 && (
          <section className="mt-24 max-w-3xl">
            <SectionHeading icon={Users} eyebrow="Who it's for" title="This toolkit was built for" />
            <ul className="space-y-3 text-neutral-700">
              {whoFor.map((w, i) => (
                <li
                  key={i}
                  data-testid={`toolkit-whofor-${i}`}
                  className="flex items-start gap-3 text-base leading-relaxed"
                >
                  <span className="w-5 h-5 rounded-full bg-brand-pink/15 text-brand-pink flex items-center justify-center text-xs flex-shrink-0 mt-0.5">{i + 1}</span>
                  {w}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* WHAT'S INSIDE */}
        {whatsInside.length > 0 && (
          <section className="mt-24 max-w-3xl">
            <SectionHeading icon={FileText} eyebrow="What's inside" title={`Here's exactly what you get`} />
            <ul className="space-y-4">
              {whatsInside.map((item, i) => (
                <li
                  key={i}
                  data-testid={`toolkit-inside-${i}`}
                  className="flex items-start gap-3 text-neutral-800"
                >
                  <Check className="w-5 h-5 text-brand-pink flex-shrink-0 mt-0.5" />
                  <span dangerouslySetInnerHTML={{ __html: item }} />
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* PREVIEW PAGE MOCKUPS */}
        {previewPages.length > 0 && (
          <section className="mt-24">
            <SectionHeading icon={Sparkles} eyebrow="A look inside" title="Preview the pages" />
            <div className="grid md:grid-cols-2 gap-6">
              {previewPages.map((pg, i) => (
                <PreviewPageCard key={i} index={i} page={pg} />
              ))}
            </div>
          </section>
        )}

        {/* FAQ */}
        {faq.length > 0 && (
          <section className="mt-24 max-w-3xl">
            <SectionHeading icon={HelpCircle} eyebrow="Questions" title="Before you buy" />
            <div className="space-y-5">
              {faq.map((f, i) => (
                <div key={i} data-testid={`toolkit-faq-${i}`} className="rounded-2xl bg-neutral-50 border border-neutral-100 p-6">
                  <div className="font-display text-xl mb-2">{f.q}</div>
                  <p className="text-neutral-600 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* STUDENT PROOF */}
        {matchedQuotes.length > 0 && (
          <section className="mt-24">
            <SectionHeading icon={MessageCircle} eyebrow="Student Proof" title="Shipped by hustlers like you" />
            <div className="grid md:grid-cols-3 gap-5">
              {matchedQuotes.map((q) => (
                <StudentQuoteCard key={q.id} post={q} />
              ))}
            </div>
            <div className="mt-6 text-xs text-neutral-500">
              Pulled live from the <Link to="/community" className="text-brand-pink hover:underline">Host Hustle community</Link>. Real posts, real students.
            </div>
          </section>
        )}

        {/* BOTTOM CTA */}
        <section className="mt-24 rounded-[2rem] bg-black text-white p-12 text-center">
          <div className="uppercase tracking-[0.3em] text-xs text-brand-pink mb-4">Ready?</div>
          <h2 className="font-display text-4xl md:text-5xl mb-4 leading-tight">
            Add <span className="italic text-brand-pink">{product.name}</span> to your toolbox.
          </h2>
          <p className="text-white/70 max-w-xl mx-auto mb-8">
            One-time ${product.price}. Downloads instantly. Lives in your toolbox forever.
          </p>
          {owned ? (
            <Link to="/toolbox">
              <Button data-testid="toolkit-footer-owned" className="rounded-full h-12 px-8 bg-white text-black hover:bg-white/90">
                <Check className="w-4 h-4 mr-2" /> Open my toolbox
              </Button>
            </Link>
          ) : (
            <Button
              onClick={buy}
              disabled={busy}
              data-testid="toolkit-footer-buy"
              className="rounded-full h-12 px-8 bg-brand-pink hover:bg-brand-pink/90 text-white btn-pink-glow"
            >
              <Download className="w-4 h-4 mr-2" /> {busy ? "Opening checkout…" : `Get it for $${product.price}`}
            </Button>
          )}
        </section>
      </section>

      <Footer />
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="rounded-2xl bg-neutral-50 border border-neutral-100 p-4">
      <div className="font-display text-2xl text-brand-pink">{value}</div>
      <div className="text-[11px] uppercase tracking-widest text-neutral-500 mt-1">{label}</div>
    </div>
  );
}

function SectionHeading({ icon: Icon, eyebrow, title }) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-brand-pink mb-3">
        <Icon className="w-3.5 h-3.5" /> {eyebrow}
      </div>
      <h2 className="font-display text-3xl md:text-4xl leading-tight">{title}</h2>
    </div>
  );
}

function StudentQuoteCard({ post }) {
  const initial = (post.author_name || "H")[0];
  const quote = post.content || "";
  const trimmed = quote.length > 220 ? quote.slice(0, 215) + "…" : quote;
  return (
    <article
      data-testid={`toolkit-quote-${post.id}`}
      className="rounded-3xl bg-white border border-neutral-100 p-6 flex flex-col h-full hover:shadow-sm transition"
    >
      <p className="text-neutral-800 leading-relaxed flex-1 italic">"{trimmed}"</p>
      <div className="mt-5 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-brand-pink/15 text-brand-pink flex items-center justify-center font-display">
          {initial}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-sm">{post.author_name}</div>
          <div className="text-[11px] uppercase tracking-widest text-neutral-500">Wins</div>
        </div>
        <div className="inline-flex items-center gap-1 text-xs text-brand-pink">
          <Heart className="w-3 h-3" fill="#E91E63" /> {post.likes}
        </div>
      </div>
    </article>
  );
}


function PreviewPageCard({ index, page }) {
  // page = { title, lines: [] }
  return (
    <div
      data-testid={`toolkit-preview-page-${index}`}
      className="group relative rounded-3xl bg-neutral-50 border border-neutral-100 p-7 hover:-translate-y-1 transition-transform duration-300"
    >
      {/* fake stacked-page shadow */}
      <div className="absolute inset-0 rounded-3xl bg-neutral-200/50 translate-x-1 translate-y-1 -z-10" aria-hidden="true" />
      <div className="absolute inset-0 rounded-3xl bg-neutral-200/30 translate-x-2 translate-y-2 -z-20" aria-hidden="true" />

      <div className="text-[10px] uppercase tracking-widest text-brand-pink mb-3">Page {index + 1}</div>
      <h3 className="font-display text-xl mb-5 leading-tight">{page.title}</h3>
      <div className="rounded-2xl bg-white border border-neutral-100 p-5 min-h-[180px]">
        <ul className="space-y-2 text-sm text-neutral-700 leading-relaxed">
          {(page.lines || []).map((line, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-brand-pink mt-0.5 text-xs">■</span>
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
